import mixpanel from 'mixpanel-browser';
import isNil from 'lodash/isNil';
import notify from '@teladoc/pulse/ui/Notification';
import {chatToggle} from '@teladoc/fe-ccm/ui/chat/chat-actions';
import {appUpdate} from '@teladoc/fe-ccm/ui/app/app-actions';
import CCMCommonUtils from '@teladoc/fe-ccm/ui/common/utilities/common-utils';
import ProtoUtils from '@teladoc/fe-ccm/ui/common/utilities/proto-utils';
import {TRACK_EVENTS_MAP} from '@teladoc/analytics';
import AmplitudeClient from '@livongo/utilities/system/amplitude';
import StorageUtils from '@livongo/utilities/system/storage';
import {
    HOME,
    EVENT_PROPS,
    BREAKPOINT_SIZE,
    BUNDLE_IDENTIFIER,
    USER_LOCALE_COOKIE,
    MESSAGE_SERVICE_TYPES,
    TRIGGER_MESSAGE_COUNTER,
    HEALTHIESTYOU_BUNDLE_IDENTIFIER,
} from '@/config';
import {
    programRoutes,
    pharmacyRoutes,
    healthMenuRoutes,
    getPrimaryRouteById,
    telemedHealthInfoRoute,
    healthInsightsReportsRoutes,
    accountRoutes,
} from '@/router/routes';
import OneAppUserAPI from '../user/user-oneapp-api';
import ProgramUtils from './program-utils';

const LOCALE_REGEX = /^[a-zA-Z]{2}-[a-zA-Z]{2}$/;

const showProgramsMenu = ({
    hasConditionManagement,
    hasMentalHealth,
    hasPartnerPrograms,
}) => {
    return hasConditionManagement || hasMentalHealth || hasPartnerPrograms;
};

const updateProgramsMenuRoutes = ({programs, appState}) => {
    const updatedRoutes = [];
    const CCMProfilePrefRoute = getPrimaryRouteById('ccmProgramsRedirect');
    const CCMSSORedirectRoute = getPrimaryRouteById('ccmSso');
    let CCMRoute = programRoutes[0];
    const MHRoute = programRoutes[1];
    const PPRoute = programRoutes[2];

    if (ProgramUtils.userHasLivongoAccount()) {
        if (!ProgramUtils.userHasActiveCCMProgram(programs)) {
            CCMRoute = CCMProfilePrefRoute;
        }
    } else {
        CCMRoute = CCMSSORedirectRoute;
    }

    if (appState.hasConditionManagement) {
        updatedRoutes.push(CCMRoute);
    }

    if (appState.hasMentalHealth) {
        updatedRoutes.push(MHRoute);
    }

    if (appState.hasPartnerPrograms) {
        updatedRoutes.push(PPRoute);
    }

    return updatedRoutes;
};

const updateAccountRoutes = ({hasCommunicationPreferencesPulse}) => {
    if (!hasCommunicationPreferencesPulse) {
        return accountRoutes.filter(
            route => route.id !== 'languageAccessibility'
        );
    }

    return [...accountRoutes];
};

const updateHealthInfoMenuRoute = ({
    hasTelemed,
    branding,
    hasCardiometabolic,
    hasCCMHealthInsights,
    hasPrescriptionSavings,
}) => {
    const isCanadaCore =
        OneAppUserAPI.getBundleIdentifier() ===
            process.env.WEBSDK_BUNDLE_IDENTIFIER_CA &&
        branding?.brand_code === 'teladoc';
    const isPLIBrand = OneAppUserAPI.isPLIBrand();
    const isCanadaInstance = OneAppUserAPI.isCanadaInstance();
    const indexToInsert = 3;
    const isRestrictedMember =
        branding?.restricted_member_correctional_facilities;

    const allRoutes = [
        ...healthMenuRoutes.slice(0, indexToInsert),
        ...(hasCCMHealthInsights && hasCardiometabolic && !isCanadaInstance
            ? healthInsightsReportsRoutes
            : []),
        ...healthMenuRoutes.slice(indexToInsert),
        ...pharmacyRoutes,
        ...(hasTelemed?.primaryCareVitals ? [telemedHealthInfoRoute[0]] : []),
        ...(hasTelemed?.expertOpinionCases ? [telemedHealthInfoRoute[1]] : []),
    ];
    const routesToSuppress = {
        prescriptionSavings: !hasPrescriptionSavings,
    };

    if (isPLIBrand || isCanadaCore) {
        routesToSuppress.careTeam = !hasTelemed?.primaryCareTeam;
        routesToSuppress.carePlan = !hasTelemed?.primaryCarePlan;
        routesToSuppress.pharmacy = isRestrictedMember;
        routesToSuppress.externalProviders = isRestrictedMember;
        // [SHINE-1275] Suppressing Activity Tracker route as per the latest requirement we only left access via link
        routesToSuppress.activityTracker = true;
    }

    return allRoutes.filter(route => !routesToSuppress[route.id]);
};

const onChatCCMOpen = ({channels, dispatch}) => {
    dispatch(chatToggle());
    mixpanel.track('navigation.button.clicked', {
        level: 2,
        button: 'Condition Management coaches',
    });

    AmplitudeClient.track(TRACK_EVENTS_MAP.navigationButtonClicked, {
        level: 2,
        category: 'navigation',
        // eslint-disable-next-line camelcase
        product_line: EVENT_PROPS.CCM,
        button: EVENT_PROPS.CCMCoaches,
    });
};

const updateSidebar = (showSidebar, dispatch) => {
    dispatch(appUpdate({isOneAppSidebar: showSidebar}));
};

const setBreakpoint = (breakpoint, dispatch) => {
    const width = window.innerWidth;
    const newBreakpoint =
        width <= 767
            ? BREAKPOINT_SIZE.Small
            : width <= 1279
            ? BREAKPOINT_SIZE.Medium
            : BREAKPOINT_SIZE.Large;

    if (breakpoint !== newBreakpoint) {
        dispatch(appUpdate({breakpoint: newBreakpoint}));
    }
};

const getMessageEligibilityStatus = (
    hasChatAvailable,
    hasMHGuideAvailable,
    hasCareTeam
) => {
    if (hasChatAvailable && !hasMHGuideAvailable && !hasCareTeam) {
        return MESSAGE_SERVICE_TYPES.ChronicCare;
    } else if (!hasChatAvailable && hasMHGuideAvailable && !hasCareTeam) {
        return MESSAGE_SERVICE_TYPES.MentalHealth;
    } else if (!hasChatAvailable && !hasMHGuideAvailable && hasCareTeam) {
        return MESSAGE_SERVICE_TYPES.CareTeam;
    } else {
        return null;
    }
};

const isOneAppLocaleCookieSet = () =>
    !isNil(CCMCommonUtils.getCookie({key: USER_LOCALE_COOKIE}));

const setOneAppLocaleCookie = value =>
    CCMCommonUtils.setCookie({key: USER_LOCALE_COOKIE, value});

const getOneAppLocaleCookie = () => {
    let locale = StorageUtils.get({
        key: USER_LOCALE_COOKIE,
        type: 'cookie',
        useNative: true,
        parse: false,
    });

    const defaultLocale = 'en-US';

    if (isNil(locale) || !LOCALE_REGEX.test(locale)) {
        return defaultLocale;
    }

    try {
        locale = locale?.split('-');
        locale?.splice(1, 1, locale[1].toUpperCase());
        locale = locale.join('-');
    } catch {
        return defaultLocale;
    }

    try {
        // see if we have a translation file for this locale
        require(`@teladoc/fe-i18n/locales/${locale}/app.json`);
    } catch {
        // eslint-disable-next-line no-console
        console.warn(`Translation file unavailable for locale ${locale}`);
        locale = defaultLocale;
    }

    return locale;
};

const isCanadaLifeBrand = brandCode =>
    brandCode === 'clsso' || brandCode === 'clnonsso';

const getLogoUrlForBrand = ({branding, width, height}) => {
    if (branding && branding?.brand_code) {
        const brandCode = isCanadaLifeBrand(branding.brand_code)
            ? 'teladoc'
            : branding.brand_code;

        return CCMCommonUtils.createCloudinaryUrl({
            src: `pli/brands/${brandCode}/logo.png`,
            width,
            height,
        });
    }
};

const getFaviconUrlForBrand = ({branding, width, height}) => {
    if (branding && branding?.brand_code) {
        return CCMCommonUtils.createCloudinaryUrl({
            src: `pli/brands/${branding.brand_code}/favicon.ico`,
            width,
            height,
        });
    }
};

const getSmallLogoUrlForBrand = ({branding, width, height}) => {
    if (branding && branding?.brand_code) {
        return CCMCommonUtils.createCloudinaryUrl({
            src: `pli/brands/${branding.brand_code}/logo-small.svg`,
            width,
            height,
        });
    }
};

const getHeaderTitleForMatchingScreen = (screenName, screenHeadersObject) => {
    let headerTitle = null;

    // Check if the screen name starts with or contains any of the keys
    Object.keys(screenHeadersObject).forEach(key => {
        if (screenName.startsWith(key) || screenName.includes(key)) {
            headerTitle = screenHeadersObject[key];
        }
    });

    return headerTitle;
};

const isOneAppDashboard = url => {
    const noProtocal = url.replace('https://', '').replace('http://', ''); // remove protocals;
    const urlArray = noProtocal?.split('/');

    if (urlArray.length > 1) {
        return urlArray[1] === HOME;
    }

    return false;
};

const validateRedirectURL = locationWithHash => {
    return (
        TRIGGER_MESSAGE_COUNTER?.find(path => locationWithHash?.includes(path))
            ?.length > 0
    );
};

const generateA1cValues = () => {
    const a1cValues = [];

    for (let i = 4; i <= 14; i += 0.1) {
        a1cValues.push({label: i.toFixed(1), value: i.toFixed(1)});
    }

    return a1cValues;
};

const selfReportedScreensSuccessToast = t => {
    return notify({
        type: 'success',
        title: t('selfReportScreen.notification.successTitle'),
        message: t('selfReportScreen.notification.successMessage'),
    });
};

const filterRoutesByBrandSuppressedRoutes = (activeRoutes, branding) => {
    const supressedRoutesByBrand = branding?.suppressed_account_routes;

    if (
        !Array.isArray(supressedRoutesByBrand) ||
        supressedRoutesByBrand.length < 1
    ) {
        return activeRoutes;
    }

    return activeRoutes.filter(
        route => !supressedRoutesByBrand.includes(route.id)
    );
};

/**
 * Determines the appropriate route based on provider group and user program data
 * @param {string} groupId - The provider group ID from the cookie
 * @param {Array} userProviderList - Array of provider groups (providerData.providersList)
 * @param {string} defaultHomeRoute - Default home route path
 * @param {string} programsRoute - Programs route path
 * @returns {string} - The route path to navigate to
 */
const determineProviderRoute = ({
    groupId,
    userProviderList,
    defaultHomeRoute = '/condition-management/home',
    programsRoute = '/condition-management/programs',
}) => {
    // If no group_id cookie, load the default home route
    if (!groupId) {
        return defaultHomeRoute;
    }

    // If providers list is not available or empty, load the default home route
    if (!Array.isArray(userProviderList) || userProviderList.length === 0) {
        return defaultHomeRoute;
    }

    // Find the provider group in providersList
    const providerGroup = userProviderList.find(
        provider => provider.providerGroupId === Number(groupId)
    );

    // If the provider group not found, load the default home route
    if (!providerGroup) {
        return defaultHomeRoute;
    }

    const {ManageSolution} = ProtoUtils;
    const {ProgramStatus} = ManageSolution || {};
    const ACTIVE_ENUM = ProgramStatus?.Values?.ACTIVE;

    // providerData.providersList typically exposes programs on `programsList`
    const programs =
        providerGroup.programsList ||
        providerGroup.programs ||
        providerGroup.programData?.programsList ||
        [];

    // Check if any programs in the provider group have ACTIVE status (enum)
    const hasActiveProgram = Array.isArray(programs)
        ? programs.some(program => {
              const status = program?.programStatus;

              return (
                  (typeof ACTIVE_ENUM !== 'undefined' &&
                      status === ACTIVE_ENUM) ||
                  status === 'ACTIVE' // fallback for any legacy/string payloads
              );
          })
        : false;

    // If at least one program is ACTIVE, load the default home route
    // Otherwise, load programs route
    return hasActiveProgram ? defaultHomeRoute : programsRoute;
};

/**
 * Builds the `amplitudeConfig` the WebSDK components hand to their SDK.
 *
 * Event tracking moved to `@teladoc/analytics` (WEBP-86); what stays here is the
 * client-bootstrap concern, since the key is brand-dependent and read off the
 * ccm store. Consumed by `Websdk.js` and `WebsdkHomepage.js`.
 *
 * When working with or testing amplitude analytics in the example app, the
 * amplitude development API key must be hardcoded. Development API keys can be
 * found on the amplitude portal, check with data science team for access.
 * DO NOT use production API key, it will pollute the production data.
 */
const getAmplitudeConfig = () => {
    const bundleIdentifier = CCMCommonUtils.getCookie({key: BUNDLE_IDENTIFIER});
    const defaultApiKey =
        window?.process?.env?.AMPLITUDE_API_KEY ??
        process.env.AMPLITUDE_API_KEY;
    const bundleApiKeys = {
        [HEALTHIESTYOU_BUNDLE_IDENTIFIER]:
            window?.process?.env?.HY_AMPLITUDE_API_KEY ??
            process.env.HY_AMPLITUDE_API_KEY,
    };
    const apiKey = bundleApiKeys[bundleIdentifier] || defaultApiKey;

    return {client: {apiKey, configuration: {minIdLength: 1}}};
};

const CommonUtils = {
    determineProviderRoute,
    getAmplitudeConfig,
    onChatCCMOpen,
    updateSidebar,
    setBreakpoint,
    showProgramsMenu,
    isOneAppDashboard,
    getLogoUrlForBrand,
    getSmallLogoUrlForBrand,
    validateRedirectURL,
    getOneAppLocaleCookie,
    getFaviconUrlForBrand,
    setOneAppLocaleCookie,
    isOneAppLocaleCookieSet,
    updateProgramsMenuRoutes,
    updateAccountRoutes,
    updateHealthInfoMenuRoute,
    getMessageEligibilityStatus,
    getHeaderTitleForMatchingScreen,
    generateA1cValues,
    selfReportedScreensSuccessToast,
    filterRoutesByBrandSuppressedRoutes,
    isIE: CCMCommonUtils.isIE,
    getCookie: CCMCommonUtils.getCookie,
    setCookie: CCMCommonUtils.setCookie,
    getSession: CCMCommonUtils.getSessionStorage,
    setSession: CCMCommonUtils.setSessionStorage,
    removeSession: CCMCommonUtils.removeSessionStorage,
    getCountry: CCMCommonUtils.getCountry,
    getTimezone: CCMCommonUtils.getTimezone,
    removeCookie: CCMCommonUtils.removeCookie,
    unicodeToChar: CCMCommonUtils.unicodeToChar,
    setLocalStorage: CCMCommonUtils.setLocalStorage,
    getLocalStorage: CCMCommonUtils.getLocalStorage,
    isFeatureEnabled: CCMCommonUtils.isFeatureEnabled,
    formatPhoneNumber: CCMCommonUtils.formatPhoneNumber,
    removeLocalStorage: CCMCommonUtils.removeLocalStorage,
    generateOneAppParams: CCMCommonUtils.generateOneAppParams,
    getCountryFromLocale: CCMCommonUtils.getCountryFromLocale,
    getCountryFromHostname: CCMCommonUtils.getCountryFromHostname,
};

export default CommonUtils;
